import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users, Plus, Video, X, BookOpen, Clock, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'GED', subjects: ['Math', 'Science', 'English', 'Social Studies'] },
  { value: 'SAT', subjects: ['SAT Math', 'SAT Reading', 'SAT Writing'] },
  { value: 'College', subjects: ['Math', 'Science', 'Business', 'Writing'] },
];

const CAT_COLORS = {
  GED: 'bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20',
  SAT: 'bg-[#E53935]/10 text-[#E53935] border-[#E53935]/20',
  College: 'bg-[#FFC107]/10 text-[#7B6100] border-[#FFC107]/30',
};

export default function GroupStudy() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSubscribed = user?.subscription_status === 'active';
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newRoom, setNewRoom] = useState({ name: '', subject: '', category: '' });
  const [availableSubjects, setAvailableSubjects] = useState([]);

  useEffect(() => {
    if (isSubscribed) fetchRooms();
    else setLoading(false);
  }, [isSubscribed]);

  const fetchRooms = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/study-rooms`, { withCredentials: true });
      setRooms(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (val) => {
    setNewRoom({ ...newRoom, category: val, subject: '' });
    const cat = CATEGORIES.find(c => c.value === val);
    setAvailableSubjects(cat ? cat.subjects : []);
  };

  const handleCreateRoom = async () => {
    if (!newRoom.name || !newRoom.category || !newRoom.subject) {
      toast.error('Please fill in all fields');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/study-rooms`, newRoom, { withCredentials: true });
      toast.success('Study room created!');
      setShowCreate(false);
      setNewRoom({ name: '', subject: '', category: '' });
      fetchRooms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create room');
    } finally {
      setCreating(false);
    }
  };

  const handleJoinRoom = async (roomId) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/study-rooms/${roomId}/join`, {}, { withCredentials: true });
      toast.success('Joined study room!');
      window.open(data.meeting_link, '_blank');
      fetchRooms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join room');
    }
  };

  const handleCloseRoom = async (roomId) => {
    if (!window.confirm('Close this study room?')) return;
    try {
      await axios.delete(`${API_URL}/api/study-rooms/${roomId}`, { withCredentials: true });
      toast.success('Room closed');
      fetchRooms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close room');
    }
  };

  if (!isSubscribed) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <Users className="w-12 h-12 text-[#002FA7]/20 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#0A0A0A] mb-3">Group Study Rooms</h2>
          <p className="text-[#4B5563] mb-6">Subscribe to create or join study rooms and collaborate with other students.</p>
          <Button onClick={() => navigate('/pricing')} className="bg-[#002FA7] hover:bg-[#001B63] rounded-sm">
            Subscribe - $500/month
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-16 md:py-24 px-4 md:px-6" data-testid="group-study-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
          <div>
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#002FA7] mb-2 block">STUDY TOGETHER</span>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-[#0A0A0A] mb-2">
              Group Study Rooms
            </h1>
            <p className="text-[#4B5563]">Create or join a room to video chat and study with other students.</p>
          </div>
          <Button
            onClick={() => setShowCreate(true)}
            className="bg-[#002FA7] hover:bg-[#001B63] rounded-sm whitespace-nowrap self-start"
            data-testid="create-room-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Create Room
          </Button>
        </div>

        {/* Active Rooms */}
        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner"></div></div>
        ) : rooms.length === 0 ? (
          <div className="bg-white border border-[#E5E7EB] rounded-sm text-center py-16">
            <Users className="w-16 h-16 text-[#002FA7]/10 mx-auto mb-4" />
            <h3 className="font-bold text-[#0A0A0A] text-lg mb-2">No Active Study Rooms</h3>
            <p className="text-[#4B5563] mb-6">Be the first to create a study room!</p>
            <Button onClick={() => setShowCreate(true)} className="bg-[#002FA7] hover:bg-[#001B63] rounded-sm">
              <Plus className="w-4 h-4 mr-2" /> Create Room
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rooms.map(room => (
              <div
                key={room.id}
                className="bg-white border border-[#E5E7EB] rounded-sm p-6 hover:shadow-md transition-shadow"
                data-testid={`study-room-${room.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-bold text-[#0A0A0A] text-lg">{room.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-sm border font-medium ${CAT_COLORS[room.category] || ''}`}>
                        {room.category}
                      </span>
                      <span className="text-sm text-[#4B5563]">{room.subject}</span>
                    </div>
                  </div>
                  {room.created_by === user?.id && (
                    <button
                      onClick={() => handleCloseRoom(room.id)}
                      className="text-[#9CA3AF] hover:text-[#E53935] transition-colors"
                      data-testid={`close-room-${room.id}`}
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2 mb-4 text-sm text-[#4B5563]">
                  <Users className="w-4 h-4" />
                  <span>{room.participants.length} participant{room.participants.length !== 1 ? 's' : ''}</span>
                  <span className="text-[#9CA3AF]">&middot;</span>
                  <span>Created by {room.created_by_name}</span>
                </div>

                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  {room.participants.map((p, i) => (
                    <span key={i} className="text-xs bg-[#F3F4F6] px-2 py-1 rounded-sm">{p}</span>
                  ))}
                </div>

                <Button
                  onClick={() => handleJoinRoom(room.id)}
                  className="w-full bg-[#002FA7] hover:bg-[#001B63] rounded-sm"
                  data-testid={`join-room-${room.id}`}
                >
                  <Video className="w-4 h-4 mr-2" /> Join Study Room
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Room Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="rounded-sm mx-4">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Create Study Room</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Room Name</label>
              <Input
                value={newRoom.name}
                onChange={(e) => setNewRoom({ ...newRoom, name: e.target.value })}
                placeholder="e.g., GED Math Study Group"
                className="rounded-sm"
                data-testid="room-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Category</label>
              <Select onValueChange={handleCategoryChange} value={newRoom.category}>
                <SelectTrigger className="rounded-sm" data-testid="room-category-select">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.value}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {availableSubjects.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Subject</label>
                <Select onValueChange={(val) => setNewRoom({ ...newRoom, subject: val })} value={newRoom.subject}>
                  <SelectTrigger className="rounded-sm" data-testid="room-subject-select">
                    <SelectValue placeholder="Select a subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableSubjects.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-sm">Cancel</Button>
            <Button
              onClick={handleCreateRoom}
              disabled={creating}
              className="bg-[#002FA7] hover:bg-[#001B63] rounded-sm"
              data-testid="confirm-create-room"
            >
              {creating ? 'Creating...' : 'Create Room'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
